import { createPaymentPage } from '@/cloudFunctions/clientToFirmPayments';
import { MatterData, MatterType } from '@/database/baseTypes';
import { FirmDatabase } from '@/database/firmDatabase';
import {
  createLitigationSubfolders,
  getOrCreateMatterFolder,
} from '../../Documents/microsoft/services/matterSharePointService';
import { AddEditMatterFormData } from '../types';
import { addOrUpdateInsuranceCases } from './addOrUpdateInsuranceCases';
import { addOrUpdateMatterContacts } from './addOrUpdateMatterContacts';
import { MissingFieldError } from './saveFormData';
import { assertUnreachable } from '@/utils/assertUnreachable';
import { constructMatterNameFromFormData } from '../constructMatterNameFromFormData';
import { telemetry } from '@/lib/telemetry';
import { isFirestoreAlreadyExistsError } from '@/utils/firestoreErrors';

export const createNewMatter = async (props: {
  firmDb: FirmDatabase;
  formData: AddEditMatterFormData;
  usePractistPayments: boolean;
  accessToken?: string;
  driveId?: string;
  onSubmitEventId: string;
}) => {
  const {
    firmDb,
    formData,
    usePractistPayments,
    accessToken,
    driveId,
    onSubmitEventId,
  } = props;

  if (!formData.state) {
    throw new MissingFieldError('State is required');
  }

  if (!formData.matterStatus) {
    throw new MissingFieldError('Matter status is required');
  }

  if (!formData.feeType) {
    throw new MissingFieldError('Fee type is required');
  }

  const matterType = matterTypeForForm(formData);

  // Create the raw matter data
  const matterData: MatterData = {
    createdTimestamp: new Date(),
    matterType,
    name:
      (formData.matterName?.trim() ?? '') !== ''
        ? (formData.matterName as string).trim()
        : constructMatterNameFromFormData(formData),
    responsibleAttorney: formData.responsibleAttorneyId ?? undefined,
    otherAssignee: formData.otherAssigneeId ?? undefined,
    state: formData.state,
    status: formData.matterStatus,
    subStatusId: formData.matterSubStatusId ?? undefined,
    description: formData.description ?? undefined,
    leadSourceId: formData.leadSourceId ?? undefined,
    practiceArea: formData.practiceAreaId ?? undefined,
    scope: formData.scope ?? undefined,
  };

  switch (formData.feeType) {
    case 'Insurance':
      // Insurance case fields are stored on an insuranceCases document.
      break;
    case 'Hourly':
      matterData.retainer = formData.retainer ?? undefined;
      matterData.responsibleAttyHourlyRate =
        formData.responsibleAttyHourlyRate ?? undefined;
      matterData.referralOriginator =
        formData.referralOriginatorId ?? undefined;
      matterData.engagementOriginator =
        formData.engagementOriginatorId ?? undefined;
      break;
    case 'FlatFee':
      matterData.flatFeeAmt = formData.flatFeeAmt ?? undefined;
      matterData.referralOriginator =
        formData.referralOriginatorId ?? undefined;
      matterData.engagementOriginator =
        formData.engagementOriginatorId ?? undefined;
      break;
    case 'Contingency':
      matterData.contingencyFees = {
        pctPreFilingOfLawsuit:
          formData.contingencyFees?.pctPreFilingOfLawsuit ?? undefined,
        pctUponFilingOfLawsuit:
          formData.contingencyFees?.pctUponFilingOfLawsuit ?? undefined,
        pctUponCommencementOfTrial:
          formData.contingencyFees?.pctUponCommencementOfTrial ?? undefined,
      };
      matterData.referralOriginator =
        formData.referralOriginatorId ?? undefined;
      matterData.engagementOriginator =
        formData.engagementOriginatorId ?? undefined;
      break;
    case `Unknown`:
      matterData.referralOriginator =
        formData.referralOriginatorId ?? undefined;
      matterData.engagementOriginator =
        formData.engagementOriginatorId ?? undefined;
      break;
    default:
      return assertUnreachable(formData);
      break;
  }

  telemetry.info({
    message: 'createNewMatter: Will call firmDb.addMatter',
    extra: {
      matterName: matterData.name,
      matterDescription: formData.description ?? 'undefined',
      matterScope: formData.scope ?? 'undefined',
      onSubmitEventId,
    },
  });
  const matterRef = await addMatterWithIdempotencyRecovery({
    firmDb,
    matterData: {
      ...matterData,
      _idempotency: { onSubmitEventId },
    } as MatterData,
    onSubmitEventId,
  });
  telemetry.info({
    message: 'createNewMatter: Did call firmDb.addMatter successfully',
    extra: {
      matterName: matterData.name,
      matterId: matterRef.id,
      matterDescription: formData.description ?? 'undefined',
      matterScope: formData.scope ?? 'undefined',
      onSubmitEventId,
    },
  });
  telemetry.info({
    message: 'createNewMatter: Will call addOrUpdateMatterContacts',
    extra: {
      matterName: matterData.name,
      matterId: matterRef.id,
      matterDescription: formData.description ?? 'undefined',
      matterScope: formData.scope ?? 'undefined',
      onSubmitEventId,
    },
  });
  await addOrUpdateMatterContacts(
    firmDb,
    matterRef.id,
    formData,
    onSubmitEventId,
  );
  telemetry.info({
    message: 'createNewMatter: Did call addOrUpdateMatterContacts successfully',
    extra: {
      matterName: matterData.name,
      matterId: matterRef.id,
      matterDescription: formData.description ?? 'undefined',
      matterScope: formData.scope ?? 'undefined',
      onSubmitEventId,
    },
  });

  if (formData.feeType === 'Insurance') {
    telemetry.info({
      message: 'createNewMatter: Will call addOrUpdateInsuranceCases',
      extra: {
        matterName: matterData.name,
        matterId: matterRef.id,
        matterDescription: formData.description ?? 'undefined',
        matterScope: formData.scope ?? 'undefined',
        onSubmitEventId,
      },
    });
    await addOrUpdateInsuranceCases({
      firmDb,
      formData,
      matterId: matterRef.id,
      onSubmitEventId,
    });
    telemetry.info({
      message:
        'createNewMatter: Did call addOrUpdateInsuranceCases successfully',
      extra: {
        matterName: matterData.name,
        matterId: matterRef.id,
        matterDescription: formData.description ?? 'undefined',
        matterScope: formData.scope ?? 'undefined',
        onSubmitEventId,
      },
    });
  }

  if (usePractistPayments) {
    await createDefaultPaymentPages({
      matterId: matterRef.id,
      matterName: matterData.name,
      matterUsState: formData.state,
      isSandbox: process.env.NODE_ENV === 'development',
      onSubmitEventId,
    });
  }

  // Create SharePoint folder for the new matter (if accessToken and driveId are provided)
  if (accessToken && driveId) {
    try {
      // Create the main matter folder
      const matterFolder = await getOrCreateMatterFolder({
        accessToken,
        driveId,
        matterId: matterRef.id,
        matterName: matterData.name,
      });
      telemetry.info({
        message: 'SharePoint matter folder found or created',
        extra: { matterName: matterData.name, matterId: matterRef.id },
      });

      // Persist the folder ID + driveId on the matter so subsequent
      // loads can fetch the folder via a strongly-consistent direct
      // lookup instead of going through the eventually-consistent
      // search-by-name path. The driveId is stored alongside so the
      // hook can detect a reconnect to a different drive. Best-effort:
      // a failed write here shouldn't prevent matter creation — the
      // documents tab will fall back to the get-or-create flow and
      // re-persist on success.
      try {
        await firmDb.setMatterFolderId(matterRef.id, 'sharePoint', {
          folderId: matterFolder.id,
          driveId,
        });
      } catch (persistErr) {
        telemetry.error({
          error: persistErr,
          message:
            'Failed to persist SharePoint folder ID on matter after creation.',
          extra: {
            matterId: matterRef.id,
            folderId: matterFolder.id,
            driveId,
            onSubmitEventId,
          },
        });
      }

      // If this is a litigation matter, also create litigation-specific subfolders
      const isLitigationMatter =
        matterData.practiceArea?.toLowerCase() === 'litigation';

      if (isLitigationMatter) {
        try {
          await createLitigationSubfolders(
            accessToken,
            driveId,
            matterFolder.id,
          );
          telemetry.info({
            message: 'Created litigation subfolders for matter',
            extra: { matterId: matterRef.id },
          });
        } catch (subfolderError) {
          telemetry.error({
            error: subfolderError,
            message: 'Failed to create litigation subfolders',
            extra: { matterId: matterRef.id },
          });
        }
      }
    } catch (err) {
      telemetry.error({
        error: err,
        message: 'Failed to create SharePoint matter folder',
        extra: { matterId: matterRef.id, onSubmitEventId },
      });
    }
  }
};

const DEFAULT_PAYMENT_PAGE_ACCOUNT_TYPES = ['operating', 'trust'] as const;

const createDefaultPaymentPages = async (props: {
  matterId: string;
  matterName: string;
  matterUsState: string;
  isSandbox: boolean;
  onSubmitEventId: string;
}) => {
  const { matterId, matterName, matterUsState, isSandbox, onSubmitEventId } =
    props;

  for (const operatingOrTrust of DEFAULT_PAYMENT_PAGE_ACCOUNT_TYPES) {
    try {
      const result = await createPaymentPage({
        operatingOrTrust,
        matterId,
        matterName,
        matterUsState,
        isSandbox,
      });

      if (result.status === 'error') {
        telemetry.warn({
          message:
            'createNewMatter: Failed to create default payment page after matter creation',
          extra: {
            matterId,
            matterName,
            matterUsState,
            isSandbox,
            operatingOrTrust,
            onSubmitEventId,
            paymentPageError: result.error,
          },
        });
      }
    } catch (error) {
      telemetry.warn({
        message:
          'createNewMatter: Failed to create default payment page after matter creation',
        extra: {
          matterId,
          matterName,
          matterUsState,
          isSandbox,
          operatingOrTrust,
          onSubmitEventId,
          ...describePaymentPageCreationError(error),
        },
      });
    }
  }
};

const describePaymentPageCreationError = (
  error: unknown,
): Record<string, unknown> => {
  const errorLike =
    error && typeof error === 'object'
      ? (error as {
          code?: unknown;
          requestId?: unknown;
          statusCode?: unknown;
        })
      : undefined;

  return {
    errorMessage: error instanceof Error ? error.message : String(error),
    errorName: error instanceof Error ? error.name : undefined,
    errorCode: typeof errorLike?.code === 'string' ? errorLike.code : undefined,
    requestId:
      typeof errorLike?.requestId === 'string'
        ? errorLike.requestId
        : undefined,
    statusCode:
      typeof errorLike?.statusCode === 'number'
        ? errorLike.statusCode
        : undefined,
  };
};

const matterTypeForForm = (formData: AddEditMatterFormData): MatterType => {
  const feeType = formData.feeType;
  switch (feeType) {
    case 'Insurance':
      const provider = formData.insuranceProvider;
      switch (provider) {
        case 'arag':
          return 'ARAG';
        case 'metlife':
          return 'MetLife';
        case 'legalease':
          return 'LegalEase';
        case 'other':
          return 'OtherInsurance';
        default:
          return 'OtherInsurance';
      }
    case 'Unknown':
      return `Don't Know`;
    default:
      return feeType;
  }
};

const addMatterWithIdempotencyRecovery = async (props: {
  firmDb: FirmDatabase;
  matterData: MatterData;
  onSubmitEventId: string;
}) => {
  const { firmDb, matterData, onSubmitEventId } = props;

  try {
    return await firmDb.addMatter(matterData);
  } catch (error) {
    if (!isFirestoreAlreadyExistsError(error)) {
      throw error;
    }

    const matchingMatters = await firmDb.getMatters([
      [
        '_idempotency.onSubmitEventId' as keyof MatterData,
        '==',
        onSubmitEventId,
      ],
    ]);

    if (matchingMatters.empty) {
      telemetry.error({
        error,
        message:
          'createNewMatter: addMatter returned already-exists, but no matter was found by onSubmitEventId lookup',
        extra: {
          onSubmitEventId,
          matchingMatterCount: matchingMatters.size,
          addMatterError: String(error),
        },
      });
      throw error;
    }

    const recoveredMatterRef = matchingMatters.docs[0].ref;
    telemetry.warn({
      message:
        'createNewMatter: addMatter returned already-exists and was recovered by onSubmitEventId lookup',
      extra: {
        onSubmitEventId,
        recoveredMatterId: recoveredMatterRef.id,
        matchingMatterCount: matchingMatters.size,
        addMatterError: String(error),
      },
    });
    return recoveredMatterRef;
  }
};
